import React, { useEffect, useState } from "react";
import closeButton from '../../assets/images/close.png';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getApprovedWorkTypeCounts } from "../../services/AppService";
import { resolveMediaUrl } from "../../utility/media";
import PageFade from "../../components/Login/PageFade";

const VoiceWorkTypePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // Debounce search input for performance
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    useEffect(() => {
        fetchDashboardData();
    }, [page, size, debouncedSearch]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            // Reusing the approved counts service as requested
            const res = await getApprovedWorkTypeCounts(debouncedSearch, page, size);
            if (res) {
                setData(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
            }
        } catch (err) {
            console.error("Failed to fetch dashboard data", err);
        } finally {
            setLoading(false);
        }
    };

    const handleViewWorkOrders = (workTypeId, workTypeName) => {
        // Points to the completion management list
        navigate(`/smartcity/voice/work-orders-voice/${workTypeId}`, {
            state: { workTypeName }
        });
    };

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6">
                {/* Header Section matches Add / Edit Work Completed Details */}
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-xl font-semibold text-blue">
                       Add / Edit Voice
                    </h4>
                    <img
                        src={closeButton}
                        alt="Close"
                        className="w-6 h-6 cursor-pointer"
                        onClick={() => navigate(-1)}
                    />
                </div>

                {/* Actions Section */}
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                    <div className="flex items-center gap-2 text-sm">
                        <label>{t('common.show')}:</label>
                        <select
                            value={size}
                            onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                            className="border rounded px-2 py-1 outline-none"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <label>{t('common.entries')}</label>
                    </div>
                    <div>
                        <input
                            type="text"
                            placeholder={t('common.search')}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                            className="border rounded px-3 py-1 text-sm outline-none w-full sm:w-64"
                        />
                    </div>
                </div>

                {/* Table Section */}
                <div className="overflow-x-auto">
                    <table className="w-full border text-sm ctable rounded-lg">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white">
                                <th className="px-3 py-2 text-center border-b border-black w-16">{t('tables.srNo')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('forms.planEstimate.workType')}</th>
                                <th className="px-3 py-2 text-center border-b border-black w-32">{t('common.total')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="3" className="text-center p-4">{t('common.loading')}</td></tr>
                            ) : data.length === 0 ? (
                                <tr><td colSpan="3" className="text-center py-4">{t('common.noRecords')}</td></tr>
                            ) : (
                                data.map((item, idx) => (
                                    <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}>
                                        <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-4 pl-10">
                                                {item.workImageUrl && (
                                                    <img
                                                        src={resolveMediaUrl(item.workImageUrl)}
                                                        alt=""
                                                        className="w-10 h-10 object-contain"
                                                    />
                                                )}
                                                <span className="font-medium text-gray-700 uppercase">{item.workTypeName}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button
                                                onClick={() => handleViewWorkOrders(item.id, item.workTypeName)}
                                                disabled={item.totalEstimates === 0}
                                                className="bg-[#3182ce] text-white font-bold w-10 h-10 rounded-full hover:bg-blue-700 shadow-md transition-all flex items-center justify-center mx-auto cursor-pointer"
                                            >
                                                {item.totalEstimates || 0}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                    <p>{t('common.showing')} {page * size + 1} {t('common.to')} {page * size + data.length} {t('common.of')} {totalElements}</p>
                    <div className="flex gap-1">
                        <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50">{t('common.previous')}</button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setPage(i)}
                                className={`px-3 py-1 border rounded ${page === i ? 'bg-[#a0adf1] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            >
                                {i + 1}
                            </button>
                        )).slice(Math.max(0, page - 2), Math.min(totalPages, page + 3))}
                        <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50">{t('common.next')}</button>
                    </div>
                </div>
            </div>
        </PageFade>
    );
};

export default VoiceWorkTypePage;